import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, Sparkles, Send, Loader2, AlertCircle } from "lucide-react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import LeadTable from "@/components/LeadTable";
import { toast } from "sonner";

export default function CampaignDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [leads, setLeads] = useState([]);
  const [outreach, setOutreach] = useState([]);
  const [campStats, setCampStats] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [tone, setTone] = useState("professional");
  const [offer, setOffer] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(null);

  const loadAll = () => {
    api.get(`/campaigns/${id}`).then((r) => setCampaign(r.data));
    api.get(`/leads`, { params: { campaign_id: id } }).then((r) => setLeads(r.data));
    api.get(`/outreach/campaign/${id}`).then((r) => setOutreach(r.data));
    api.get(`/stats/campaign/${id}`).then((r) => setCampStats(r.data)).catch(() => setCampStats(null));
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);

  const drafts = outreach.length;
  const sent = outreach.filter((e) => e.sent).length;
  const unsent = drafts - sent;
  const now = new Date();
  const due = outreach.filter(e => !e.sent && new Date(e.send_after) <= now).length;
  const scheduled = outreach.filter(e => !e.sent && new Date(e.send_after) > now).length;

  const exportCsv = async () => {
    const token = localStorage.getItem("lf_token");
    const res = await fetch(`${API}/leads/export/csv?campaign_id=${id}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `prospects-${id}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const bulkGenerate = async () => {
    setBusy("generate");
    try {
      const r = await api.post(`/outreach/campaign/${id}/bulk-generate`, { tone, offer, overwrite });
      toast.success(`Drafted ${r.data.generated} · skipped ${r.data.skipped} · failed ${r.data.failed}`);
      setBulkOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk drafting failed");
    } finally { setBusy(null); }
  };

  const sendAll = async () => {
    if (!user?.has_smtp) { toast.error("Configure your email provider in Settings first"); return; }
    if (!user?.from_email) { toast.error("Configure your From email in Settings first"); return; }
    if (!window.confirm(`Send ${unsent} cold emails now?`)) return;
    setBusy("send");
    try {
      const r = await api.post(`/outreach/campaign/${id}/send-all`);
      toast.success(`Sent ${r.data.sent} of ${r.data.total} · ${r.data.failed} failed`);
      if (r.data.failed > 0 && r.data.errors?.length) console.error("Send errors:", r.data.errors);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send-all failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-7xl mx-auto">
      <Link to="/app/campaigns" className="text-sm text-[#5C5C66] hover:text-[#0A0A0F] inline-flex items-center gap-2 mb-6 transition-colors" data-testid="back-to-campaigns">
        <ArrowLeft className="h-4 w-4" /> All campaigns
      </Link>
      {campaign && (
        <>
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <div className="label-mono mb-2">— Campaign</div>
              <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">{campaign.name}</h1>
              <div className="text-[#5C5C66] mt-2">{campaign.niche}{campaign.location ? ` · ${campaign.location}` : ""}{campaign.industry ? ` · ${campaign.industry}` : ""}</div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button onClick={exportCsv} variant="outline" className="rounded-lg border-[#EAEAE3] hover:bg-[#F4F4EE]" data-testid="export-csv-btn">
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
              <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-lg border-[#EAEAE3] hover:bg-[#F4F4EE]" data-testid="bulk-generate-btn">
                    <Sparkles className="h-4 w-4 mr-2" /> Draft outreach
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md rounded-2xl">
                  <DialogHeader><DialogTitle className="font-display tracking-tighter text-2xl">Bulk-draft cold emails</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <p className="text-sm text-[#5C5C66]">We'll draft a personalised email for every prospect in this campaign. Review and ship when ready.</p>
                    <div>
                      <Label className="label-mono">Tone</Label>
                      <Select value={tone} onValueChange={setTone}>
                        <SelectTrigger className="mt-2 rounded-lg" data-testid="bulk-tone"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["professional", "casual", "bold", "friendly"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="label-mono">Your offer / value prop</Label>
                      <Input value={offer} onChange={(e) => setOffer(e.target.value)} placeholder="Pipeline platform that books 30 calls/mo" className="mt-2 rounded-lg" data-testid="bulk-offer" />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="bulk-overwrite" />
                      Overwrite existing drafts
                    </label>
                  </div>
                  <DialogFooter>
                    <Button onClick={bulkGenerate} disabled={busy === "generate"} className="rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white" data-testid="bulk-generate-submit">
                      {busy === "generate" ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Drafting…</>) : "Draft all"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Button onClick={sendAll} disabled={busy === "send" || unsent === 0} className="rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white shadow-soft" data-testid="send-all-btn">
                {busy === "send" ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>) : (<><Send className="h-4 w-4 mr-2" /> Send all{unsent ? ` (${unsent})` : ""}</>)}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatTile label="Prospects" value={leads.length} />
            <StatTile label="Drafts" value={drafts} />
            <StatTile label="Sent" value={sent} accent="green" />
            <StatTile label="Pending" value={unsent} accent="amber" />
          </div>

          {sent > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatTile label="Open rate" value={`${campStats?.open_rate ?? 0}%`} accent="blue" />
              <StatTile label="Reply rate" value={`${campStats?.reply_rate ?? 0}%`} accent="orange" />
              <StatTile label="Click rate" value={`${campStats?.click_rate ?? 0}%`} />
              <StatTile label="Bounces" value={campStats?.bounced ?? 0} accent={campStats?.bounced ? "red" : null} />
            </div>
          )}

          {!user?.has_smtp && drafts > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3 text-sm">
              <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-medium text-amber-900">Connect your email provider to enable sending</div>
                <div className="text-amber-800">Drafts are ready. Go to <Link to="/app/settings" className="underline font-medium">Settings → Email Provider</Link> to add your SMTP credentials (Brevo recommended).</div>
              </div>
            </div>
          )}

          {user?.has_smtp && drafts > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start gap-3 text-sm">
              <Sparkles className="h-4 w-4 text-blue-700 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-medium text-blue-900">Ensure high deliverability</div>
                <div className="text-blue-800">Your sequences are running in the background. Ensure your domain has SPF, DKIM, and DMARC configured to prevent emails from landing in spam. <a href="#" className="underline font-medium">Read deliverability guide →</a></div>
              </div>
            </div>
          )}

          <LeadTable leads={leads} onRefresh={loadAll} />
        </>
      )}
    </div>
  );
}

function StatTile({ label, value, accent }) {
  const color = accent === "green" ? "text-[#16A34A]" :
                accent === "amber" ? "text-[#F59E0B]" :
                accent === "blue" ? "text-[#002FA7]" :
                accent === "orange" ? "text-[#FF5722]" :
                accent === "red" ? "text-red-600" :
                "text-[#0A0A0F]";
  return (
    <div className="bg-white border border-[#EAEAE3] rounded-2xl p-5 shadow-soft">
      <div className="label-mono mb-2">{label}</div>
      <div className={`font-display text-3xl font-black tracking-tighter ${color}`}>{value}</div>
    </div>
  );
}
